import React from 'react';
import { TableBody, TableCell, TableRow } from '@mui/material';
import { Link } from 'react-router-dom';

import mappedIcon from 'assets/images/mappedicon.svg';
import { StoreCell } from 'components/tables/common';
import TableLoading from 'components/tables/common/TableLoading';
import StoreTableModel from 'models/store/StoreTableModel';
import { Store, StoreTableProps } from 'types';
import { formatCurrency } from 'utils';
import {
  buildStoreReconciliationStatus,
  canInvestigateScratcher,
  getStoreSourceValue,
  StoreReconciliationStatus,
} from '../utils/reconciliationDisplay';

interface StoreTableBodyProps {
  data: StoreTableProps['data'];
  isLoading: boolean;
  isSingleDay: boolean;
  isSuperAdmin: boolean;
  onInvestigate: (row: StoreTableProps['data'][number]) => void;
}

interface MetricRow {
  id: string;
  label: string;
  expected: React.ReactNode;
  reported: React.ReactNode;
  variance: number | null;
  format: (value: number) => string;
  title?: string;
}

const formatCount = (value: number): string => value.toLocaleString('en-US');

const formatSignedValue = (value: number, formatter: (amount: number) => string): string => {
  if (value > 0) return `+${formatter(Math.abs(value))}`;
  if (value < 0) return `-${formatter(Math.abs(value))}`;
  return formatter(0);
};

const formatAuditDateTime = (value: string | null, timezone: string | null): string | null => {
  if (!value) return null;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: timezone || undefined,
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(date).replace(',', '');
  } catch {
    return new Intl.DateTimeFormat('en-US', {
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(date).replace(',', '');
  }
};

const buildStatus = (
  storeModel: StoreTableModel,
  isPosConnected: boolean,
  isLottoConnected: boolean
): StoreReconciliationStatus => buildStoreReconciliationStatus({
  isAuditDataMissing: storeModel.isAuditDataMissing,
  isPosDataMissing: storeModel.isPosDataMissing,
  isLottoDataMissing: storeModel.isLottoDataMissing,
  isPosConnected,
  isLottoConnected,
  totalVariance: storeModel.getAllDiscrepanciesTotal(),
});

const buildMetricRows = (
  storeModel: StoreTableModel,
  isPosConnected: boolean,
  isLottoConnected: boolean
): MetricRow[] => {
  const auditAvailable = !storeModel.isAuditDataMissing;
  const posAvailable = isPosConnected && !storeModel.isPosDataMissing;
  const lottoAvailable = isLottoConnected && !storeModel.isLottoDataMissing;
  const payoutsExpected = storeModel.lottoPayoutsTotal + storeModel.scratcherFreeTicketsPaidAmount;

  return [
    {
      id: 'lotto',
      label: 'Lotto',
      expected: lottoAvailable
        ? formatCurrency(storeModel.lottoSales)
        : getStoreSourceValue(isLottoConnected, storeModel.isLottoDataMissing, 'n/a'),
      reported: posAvailable
        ? formatCurrency(storeModel.posLottoSales)
        : getStoreSourceValue(isPosConnected, storeModel.isPosDataMissing, 'n/a'),
      variance: lottoAvailable && posAvailable ? storeModel.lottoSalesVariance ?? 0 : null,
      format: formatCurrency,
      title: 'POS reported lotto sales minus lottery-reported sales',
    },
    {
      id: 'payouts',
      label: 'Payouts',
      expected: lottoAvailable
        ? formatCurrency(payoutsExpected)
        : getStoreSourceValue(isLottoConnected, storeModel.isLottoDataMissing, 'n/a'),
      reported: posAvailable
        ? formatCurrency(storeModel.posPayout)
        : getStoreSourceValue(isPosConnected, storeModel.isPosDataMissing, 'n/a'),
      variance: lottoAvailable && posAvailable ? storeModel.payoutVariance ?? 0 : null,
      format: formatCurrency,
      title: 'Lottery payouts plus free tickets minus POS-reported payouts',
    },
    {
      id: 'scratcher-qty',
      label: 'Scratcher Qty',
      expected: auditAvailable ? formatCount(storeModel.totalSold) : '-',
      reported: posAvailable
        ? formatCount(storeModel.posScratcherCount)
        : getStoreSourceValue(isPosConnected, storeModel.isPosDataMissing, 'n/a'),
      variance: auditAvailable && posAvailable ? storeModel.quantityVariance ?? 0 : null,
      format: formatCount,
      title: 'POS-reported scratcher quantity minus audited quantity',
    },
    {
      id: 'scratcher-sales',
      label: 'Scratcher $',
      expected: auditAvailable ? formatCurrency(storeModel.revenue) : '-',
      reported: posAvailable
        ? formatCurrency(storeModel.posScratcherSales)
        : getStoreSourceValue(isPosConnected, storeModel.isPosDataMissing, 'n/a'),
      variance: auditAvailable && posAvailable ? storeModel.revenueVariance ?? 0 : null,
      format: formatCurrency,
      title: 'POS-reported scratcher sales minus audited scratcher sales',
    },
  ];
};

const MetricCell: React.FC<{
  rows: MetricRow[];
  column: 'label' | 'expected' | 'reported' | 'variance';
  to?: string;
}> = ({
  rows,
  column,
  to,
}) => {
  const content = (
    <div className="store-comparison-table__cell-grid">
      {rows.map((metric) => {
        const varianceTone = metric.variance == null
          ? 'unavailable'
          : metric.variance > 0
            ? 'surplus'
            : metric.variance < 0
              ? 'deficit'
              : 'matched';

        return (
          <React.Fragment key={metric.id}>
            <div
              className={`store-comparison-table__metric-value store-comparison-table__metric-value--${column}`}
              title={column === 'variance' ? metric.title : undefined}
            >
              {column === 'variance' ? (
                metric.variance == null ? (
                  <span className="store-comparison-table__unavailable">-</span>
                ) : metric.variance === 0 ? (
                  <img src={mappedIcon} alt="matched" className="store-comparison-table__matched-icon" />
                ) : (
                  <span className={`store-comparison-table__variance store-comparison-table__variance--${varianceTone}`}>
                    {formatSignedValue(metric.variance, metric.format)}
                  </span>
                )
              ) : metric[column] === 'n/a' ? (
                <span className="store-comparison-table__metric-na">n/a</span>
              ) : metric[column]}
            </div>
            {metric.id === 'payouts' && (
              <div
                aria-hidden="true"
                className={`store-comparison-table__section-divider store-comparison-table__section-divider--${column}`}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );

  return (
    <TableCell className={`store-comparison-table__cell store-comparison-table__cell--${column}`}>
      {to ? (
        <Link to={to} className="store-table-link store-comparison-table__cell-link">
          {content}
        </Link>
      ) : content}
    </TableCell>
  );
};

const StoreTableBody: React.FC<StoreTableBodyProps> = ({
  data,
  isLoading,
  isSingleDay,
  isSuperAdmin,
  onInvestigate,
}) => {
  if (isLoading) {
    return <TableLoading colSpan={5} />;
  }

  if (!data || data.length === 0) {
    return (
      <TableBody>
        <TableRow>
          <TableCell colSpan={5}>
            <p className="text-align-center">No data found</p>
          </TableCell>
        </TableRow>
      </TableBody>
    );
  }

  return (
    <TableBody>
      {data.map((row) => {
        const storeModel = StoreTableModel.fromServerData(row);
        const storeFromResponse = row?.store || row;
        const storeForCell: Store = {
          ...storeFromResponse,
          id: storeModel.id,
          name: storeModel.name,
          logo_image_url: storeModel.logoImageUrl,
          logo_url: storeModel.logoImageUrl,
          city: storeFromResponse?.city || storeModel.address?.split(',')[1]?.trim() || '',
          state: storeFromResponse?.state || storeModel.address?.split(',')[2]?.trim() || '',
          phone_number: storeFromResponse?.phone_number || '',
          email: storeFromResponse?.email || '',
          created_at: storeFromResponse?.created_at || '',
          updated_at: storeFromResponse?.updated_at || '',
          status: (storeFromResponse?.status || 'active') as Store['status'],
          subscription_status: storeModel.subscriptionStatus as Store['subscription_status'],
          pos_type: storeModel.posType,
          lotto_authorized: storeModel.lottoAuthorized,
        };
        const storeDetailPath = storeForCell.id ? `/store/${storeForCell.id}` : undefined;
        const isPosConnected = Boolean(storeModel.posType && storeModel.posType !== 'n/a');
        const isLottoConnected = storeModel.lottoAuthorized;
        const showInvestigationLink = canInvestigateScratcher({
          isSingleDay,
          isSuperAdmin,
          storeUserRole: storeFromResponse?.user_role,
          auditCount: storeModel.auditCount,
          isAuditDataMissing: storeModel.isAuditDataMissing,
          isPosConnected,
          isPosDataMissing: storeModel.isPosDataMissing,
          quantityVariance: storeModel.quantityVariance,
          revenueVariance: storeModel.revenueVariance,
        });
        const status = buildStatus(storeModel, isPosConnected, isLottoConnected);
        const metricRows = buildMetricRows(storeModel, isPosConnected, isLottoConnected);
        const windowStart = formatAuditDateTime(storeModel.auditWindowStart, storeModel.timezone);
        const windowEnd = formatAuditDateTime(storeModel.auditWindowEnd, storeModel.timezone);
        const auditCountLabel = `${storeModel.auditCount} ${storeModel.auditCount === 1 ? 'Audit' : 'Audits'}`;
        const auditWindowLabel = windowStart && windowEnd
          ? `${windowStart} → ${windowEnd}`
          : storeModel.auditCount > 0
            ? 'Audit window unavailable'
            : null;

        return (
          <TableRow
            key={storeModel.id}
            className={`store-comparison-table__row store-comparison-table__row--${status.tone}`}
          >
            <StoreCell
              row={storeForCell}
              cellStyle={{ className: 'store-comparison-table__store-cell' }}
              linkTo={storeDetailPath}
            >
              <div className="store-comparison-table__store-summary">
                <div className="store-comparison-table__audit-meta">
                  {storeDetailPath ? (
                    <Link to={storeDetailPath} className="store-table-link store-comparison-table__audit-detail-link">
                      <span className="store-comparison-table__audit-count">{auditCountLabel}</span>
                      {auditWindowLabel && (
                        <span className="store-comparison-table__audit-window">{auditWindowLabel}</span>
                      )}
                    </Link>
                  ) : (
                    <>
                      <span className="store-comparison-table__audit-count">{auditCountLabel}</span>
                      {auditWindowLabel && (
                        <span className="store-comparison-table__audit-window">{auditWindowLabel}</span>
                      )}
                    </>
                  )}
                  {showInvestigationLink && (
                    <button
                      type="button"
                      className="store-comparison-table__investigate-button"
                      onClick={(event) => {
                        event.stopPropagation();
                        onInvestigate(row);
                      }}
                    >
                      <span>Investigate</span>
                      <span>Scratcher Variance</span>
                    </button>
                  )}
                </div>
                {status.tone !== 'unavailable' && (
                  storeDetailPath ? (
                    <Link
                      to={storeDetailPath}
                      className={`store-table-link store-comparison-table__status store-comparison-table__status--${status.tone}`}
                    >
                      <span>{status.label}</span>
                      {status.detail && <strong>{status.detail}</strong>}
                    </Link>
                  ) : (
                    <div className={`store-comparison-table__status store-comparison-table__status--${status.tone}`}>
                      <span>{status.label}</span>
                      {status.detail && <strong>{status.detail}</strong>}
                    </div>
                  )
                )}
              </div>
            </StoreCell>

            <MetricCell rows={metricRows} column="label" to={storeDetailPath} />
            <MetricCell rows={metricRows} column="expected" to={storeDetailPath} />
            <MetricCell rows={metricRows} column="reported" to={storeDetailPath} />
            <MetricCell rows={metricRows} column="variance" to={storeDetailPath} />
          </TableRow>
        );
      })}
    </TableBody>
  );
};

export default StoreTableBody;
